import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Botao,
  Campo,
  CampoDeSelecao,
  Modal,
  Selo,
  useDialogo,
  type TomSemantico,
} from '../../componentes/ui';
import {
  CORES_DE_TAG,
  criarTag,
  excluirQuadro,
  excluirTag,
  renomearQuadro,
  type TagDoCrm,
} from '../../servicos/crm';
import { CHAVE_DAS_AUTOMACOES, carregarAutomacoes } from '../../servicos/automacoes';
import { ErroDaApi } from '../ErroDaApi';

/**
 * Configurações do pipeline — o quadro aberto (renomear, excluir), as tags
 * (criar, excluir), que valem para todos os quadros, e as automações (só
 * leitura: o que cada uma faz e se a etapa de que ela depende existe). Mora
 * atrás da engrenagem do Pipeline para o topo da tela ficar com o que se usa
 * todo dia. As etapas se mexem no próprio quadro, na coluna.
 *
 * As de serviço seguem o catálogo (Cadastros › Serviços): renomear o serviço
 * renomeia a tag, desativá-lo a tira do quadro. Não se excluem por aqui —
 * voltariam na próxima abertura, porque o serviço continua lá. A tela diz isso
 * no lugar do botão, em vez de oferecer uma exclusão que não pega.
 */
export function ConfiguracoesDoPipeline(props: {
  readonly aberto: boolean;
  /** O quadro aberto, com quantos cards tem — quadro com card não se exclui. */
  readonly quadro: { readonly id: string; readonly nome: string; readonly cards: number };
  /** O CRM fica sempre com ao menos um quadro. */
  readonly unicoQuadro: boolean;
  readonly aoExcluirQuadro: () => void;
  readonly tags: readonly TagDoCrm[];
  /** Quantos cards usam cada tag — o aviso da exclusão cita o número. */
  readonly usos: ReadonlyMap<string, number>;
  readonly aoFechar: () => void;
}): JSX.Element {
  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Configurações do pipeline"
      largura="larga"
      rodape={
        <Botao variante="secundario" onClick={props.aoFechar}>
          Fechar
        </Botao>
      }
    >
      <div className="flex flex-col gap-8">
        <SecaoDoQuadro
          quadro={props.quadro}
          unicoQuadro={props.unicoQuadro}
          aoExcluirQuadro={props.aoExcluirQuadro}
          descricao="O quadro aberto. As etapas se mexem no próprio quadro, pelo menu de cada coluna."
          sincronizar={props.aberto}
        />
        <SecaoDeTags tags={props.tags} usos={props.usos} />
        <SecaoDeAutomacoes ativa={props.aberto} />
      </div>
    </Modal>
  );
}

/**
 * Renomear e excluir um quadro. Na engrenagem do Pipeline, o quadro aberto; na
 * Central de Configurações, o escolhido no seletor.
 */
export function SecaoDoQuadro(props: {
  readonly quadro: { readonly id: string; readonly nome: string; readonly cards: number };
  readonly unicoQuadro: boolean;
  readonly aoExcluirQuadro: () => void;
  readonly descricao: string;
  /** Quando volta a valer (a janela reabre), o campo recomeça do nome gravado. */
  readonly sincronizar?: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const recarregar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['crm'] });
  };

  const [nomeDoQuadro, setNomeDoQuadro] = useState(props.quadro.nome);
  // Abrir de novo, ou em outro quadro, começa do nome gravado.
  useEffect(() => {
    if (props.sincronizar !== false) setNomeDoQuadro(props.quadro.nome);
  }, [props.sincronizar, props.quadro.nome]);
  const renomear = useMutation({
    mutationFn: () => renomearQuadro(props.quadro.id, nomeDoQuadro.trim()),
    onSettled: recarregar,
  });
  const apagarQuadro = useMutation({
    mutationFn: () => excluirQuadro(props.quadro.id),
    onSuccess: props.aoExcluirQuadro,
    onSettled: recarregar,
  });
  const motivoParaNaoExcluir =
    props.quadro.cards > 0
      ? `Tem ${props.quadro.cards} ${props.quadro.cards === 1 ? 'card' : 'cards'}: tire as empresas dele antes.`
      : props.unicoQuadro
        ? 'É o único quadro: o CRM precisa de ao menos um.'
        : null;

  return (
    <section aria-labelledby="titulo-quadro" className="flex flex-col gap-4">
      <div>
        <h3 id="titulo-quadro" className="text-sm font-semibold text-conteudo-primario">
          Quadro
        </h3>
        <p className="mt-1 text-sm text-conteudo-secundario">{props.descricao}</p>
      </div>
      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (nomeDoQuadro.trim() !== '' && nomeDoQuadro.trim() !== props.quadro.nome) {
            renomear.mutate();
          }
        }}
      >
        <Campo
          rotulo="Nome do quadro"
          className="min-w-0 flex-1"
          maxLength={60}
          value={nomeDoQuadro}
          onChange={(e) => setNomeDoQuadro(e.target.value)}
        />
        <Botao
          type="submit"
          variante="secundario"
          disabled={nomeDoQuadro.trim() === '' || nomeDoQuadro.trim() === props.quadro.nome}
          carregando={renomear.isPending}
        >
          Renomear
        </Botao>
      </form>
      <div className="flex flex-wrap items-center gap-3">
        <Botao
          variante="perigo"
          tamanho="pequeno"
          disabled={motivoParaNaoExcluir !== null}
          carregando={apagarQuadro.isPending}
          onClick={async () => {
            const ok = await dialogo.confirmar({
              titulo: `Excluir o quadro "${props.quadro.nome}"?`,
              descricao: 'O quadro e as etapas dele somem. Ele não tem cards.',
              rotuloConfirmar: 'Excluir',
              tom: 'perigo',
            });
            if (ok) apagarQuadro.mutate();
          }}
        >
          Excluir quadro
        </Botao>
        {motivoParaNaoExcluir !== null && (
          <span className="text-xs text-conteudo-sutil">{motivoParaNaoExcluir}</span>
        )}
      </div>
      {(renomear.error ?? apagarQuadro.error) !== null && (
        <ErroDaApi
          titulo="Não foi possível salvar o quadro"
          erro={renomear.error ?? apagarQuadro.error}
        />
      )}
    </section>
  );
}

/** As tags do CRM, que valem para todos os quadros: criar e excluir. */
export function SecaoDeTags(props: {
  readonly tags: readonly TagDoCrm[];
  /** Quantos cards usam cada tag — o aviso da exclusão cita o número. */
  readonly usos: ReadonlyMap<string, number>;
  /** De onde vem a contagem: "no quadro aberto", "neste quadro". */
  readonly contagemDe?: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState<TomSemantico>('informativo');

  const recarregar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['crm'] });
  };
  const criar = useMutation({
    mutationFn: () => criarTag(nome.trim(), cor),
    onSuccess: () => setNome(''),
    onSettled: recarregar,
  });
  const apagar = useMutation({ mutationFn: excluirTag, onSettled: recarregar });

  return (
    <section aria-labelledby="titulo-tags" className="flex flex-col gap-5">
      <div>
        <h3 id="titulo-tags" className="text-sm font-semibold text-conteudo-primario">
          Tags
        </h3>
        <p className="mt-1 text-sm text-conteudo-secundario">
          Valem para todos os quadros. As de serviço vêm do catálogo (Cadastros › Serviços), uma por
          serviço: mudam de nome com ele e saem quando ele é desativado. As demais você cria para
          marcar o que quiser.
          {props.contagemDe !== undefined && ` A contagem de cards é ${props.contagemDe}.`}
        </p>
      </div>
      <ul className="flex flex-col divide-y divide-borda/60 rounded-card border border-borda">
        {props.tags.map((tag) => {
          const usos = props.usos.get(tag.id) ?? 0;
          return (
            <li key={tag.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="flex items-center gap-2">
                <Selo tom={tag.cor} tamanho="pequeno" semPonto>
                  {tag.nome}
                </Selo>
                <span className="text-xs text-conteudo-sutil">
                  {usos} {usos === 1 ? 'card' : 'cards'}
                </span>
              </span>
              {tag.deServico ? (
                <span className="text-xs text-conteudo-sutil">Tag de serviço</span>
              ) : (
                <button
                  type="button"
                  className="text-xs text-erro underline-offset-2 hover:underline"
                  onClick={async () => {
                    const ok = await dialogo.confirmar({
                      titulo: `Excluir a tag "${tag.nome}"?`,
                      descricao:
                        usos === 0
                          ? 'Nenhum card usa esta tag.'
                          : `Ela sai de ${usos} ${usos === 1 ? 'card' : 'cards'}.`,
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    });
                    if (ok) apagar.mutate(tag.id);
                  }}
                >
                  Excluir
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (nome.trim() !== '') criar.mutate();
        }}
      >
        <Campo
          rotulo="Nova tag"
          className="min-w-0 flex-1"
          maxLength={60}
          placeholder="Ex.: Prioridade, Aguardando documento"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <CampoDeSelecao
          rotulo="Cor"
          className="sm:w-36"
          value={cor}
          onChange={(e) => setCor(e.target.value as TomSemantico)}
          opcoes={CORES_DE_TAG.map((c) => ({ valor: c.tom, rotulo: c.rotulo }))}
        />
        <Botao type="submit" disabled={nome.trim() === ''} carregando={criar.isPending}>
          Criar
        </Botao>
      </form>

      {(criar.error ?? apagar.error) !== null && (
        <ErroDaApi titulo="Não foi possível salvar a tag" erro={criar.error ?? apagar.error} />
      )}
    </section>
  );
}

/**
 * As automações do CRM (D-016), em resumo: o nome e se está ligada. Criar e
 * mudar é na Central de Configurações, aba Automações.
 */
export function SecaoDeAutomacoes(props: { readonly ativa: boolean }): JSX.Element {
  const lista = useQuery({
    queryKey: CHAVE_DAS_AUTOMACOES,
    queryFn: carregarAutomacoes,
    enabled: props.ativa,
  });

  return (
    <section aria-labelledby="titulo-automacoes" className="flex flex-col gap-4">
      <div>
        <h3 id="titulo-automacoes" className="text-sm font-semibold text-conteudo-primario">
          Automações
        </h3>
        <p className="mt-1 text-sm text-conteudo-secundario">
          O que o pipeline faz sozinho quando algo acontece nos serviços. Para criar, mudar ou
          desligar, vá a{' '}
          <Link
            to="/configuracoes?aba=automacoes"
            className="text-acento-ciano underline-offset-4 hover:underline"
          >
            Configurações › Automações
          </Link>
          .
        </p>
      </div>

      {lista.isError ? (
        <ErroDaApi titulo="Não foi possível ler as automações" erro={lista.error} />
      ) : lista.data === undefined ? (
        <p className="text-sm text-conteudo-sutil">Carregando…</p>
      ) : lista.data.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">Nenhuma automação.</p>
      ) : (
        <ol className="flex flex-col divide-y divide-borda/60 rounded-card border border-borda">
          {lista.data.map((a) => (
            <li key={a.id} className="flex flex-col gap-1 px-3 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-conteudo-primario">{a.nome}</p>
                <Selo
                  tom={
                    a.situacao === 'ATIVA'
                      ? 'positivo'
                      : a.situacao === 'PARADA'
                        ? 'atencao'
                        : 'neutro'
                  }
                  tamanho="pequeno"
                >
                  {a.situacao === 'ATIVA'
                    ? 'Ligada'
                    : a.situacao === 'PARADA'
                      ? 'Parada'
                      : 'Desligada'}
                </Selo>
              </div>
              <p className="text-xs leading-relaxed text-conteudo-secundario">{a.descricao}</p>
              {a.problemas.map((p) => (
                <p key={p} className="text-xs text-conteudo-primario">
                  {p}
                </p>
              ))}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
